import { FormDialog } from '@/components/FormDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect, SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItemTestId, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ProductType } from '@/constants/product'
import { useGetDepartmentOptions } from '@/features/departments/hooks/useDepartment'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { formatBuildingFloorLocation } from '@/utils/location'
import { getMutationOptions } from '@/utils/mutation'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateProduct, useGetProduct, useGetProductCategories, useUpdateProduct } from '../hooks/useProduct'

type ProductForm = {
  name: string
  brand: string
  model: string
  categoryId: string
  ownerDepartmentId: string
  type: ProductType
  supportsOnlineStatus: boolean
  description: string
}

export function ProductDialog({
  open,
  onOpenChange,
  productId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  productId?: string | null
}) {
  const { t } = useTranslation()
  const { data: configs } = useGetConfigs()
  const { data: productCategories = [], isFetching: isFetchingProductCategories } = useGetProductCategories(
    undefined,
    open,
  )
  const { data: departments, isFetching: isFetchingDepartments } = useGetDepartmentOptions(undefined, open)
  const { data: product, isFetching: isFetchingProduct } = useGetProduct(productId!, !!productId && open)

  const createProduct = useCreateProduct()
  const updateProduct = useUpdateProduct()

  const [form, setForm] = useState<ProductForm>({
    name: '',
    brand: '',
    model: '',
    categoryId: 'Ventilator',
    ownerDepartmentId: '',
    type: 'Durable',
    supportsOnlineStatus: false,
    description: '',
  })

  const isEdit = !!productId
  const isInvalid = !form.brand || !form.model || !form.categoryId || !form.ownerDepartmentId
  const isLoading = isFetchingProduct || createProduct.isPending || updateProduct.isPending

  const warehouseDepartmentId = configs?.[AppConfigKey.WarehouseDepartmentId]
  const departmentGroups = useMemo<SearchableSelectGroup[]>(() => {
    const departmentOptions =
      departments?.map(department => ({
        value: department.id,
        label: department.name,
        description: formatBuildingFloorLocation(t, department.buildingName, department.floorName),
        searchValue: `${department.name} ${department.buildingName || ''} ${department.floorName || ''}`,
      })) ?? []

    return [
      {
        label: t('scans.mainWarehouse'),
        options: departmentOptions.filter(department => department.value === warehouseDepartmentId),
      },
      {
        label: t('scans.otherDepartments'),
        options: departmentOptions.filter(department => department.value !== warehouseDepartmentId),
      },
    ]
  }, [departments, t, warehouseDepartmentId])

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name,
        brand: product.brand,
        model: product.model,
        categoryId: product.categoryId,
        ownerDepartmentId: product.ownerDepartmentId,
        type: product.type as ProductType,
        supportsOnlineStatus: product.supportsOnlineStatus,
        description: product.description,
      })
    } else {
      setForm({
        name: '',
        brand: '',
        model: '',
        categoryId: '',
        ownerDepartmentId: '',
        type: 'Durable',
        supportsOnlineStatus: false,
        description: '',
      })
    }
  }, [product, open])

  function handleSubmit() {
    if (isInvalid) return

    const options = getMutationOptions({
      successMessage: isEdit ? t('equipment.model.toast.editSuccess') : t('equipment.model.toast.addSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    if (isEdit && product) {
      updateProduct.mutate({ id: product.id, payload: form }, options)
    } else {
      createProduct.mutate({ payload: form }, options)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('equipment.model.edit') : t('equipment.model.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      contentTestId="product-dialog"
      submitTestId="product-form-submit"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.category')} *</Label>
          <Select
            value={form.categoryId}
            onValueChange={v => setForm({ ...form, categoryId: v })}
            disabled={isFetchingProductCategories}
          >
            <SelectTrigger data-testid="product-form-category" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('common.placeholder.selectCategory')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {productCategories.map(c => (
                <SelectItemTestId key={c.id} value={c.id} testId={`product-form-category-option-${c.id}`}>
                  {c.name}
                </SelectItemTestId>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.name')}</Label>
          <Input
            data-testid="product-form-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.brand')} *</Label>
          <Input
            data-testid="product-form-brand"
            value={form.brand}
            onChange={e => setForm({ ...form, brand: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.model')} *</Label>
          <Input
            data-testid="product-form-model"
            value={form.model}
            onChange={e => setForm({ ...form, model: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.ownerDepartment')} *</Label>
          <SearchableSelect
            value={form.ownerDepartmentId}
            onValueChange={ownerDepartmentId => setForm({ ...form, ownerDepartmentId })}
            groups={departmentGroups}
            placeholder={t('common.placeholder.selectDepartment')}
            searchPlaceholder={t('common.placeholder.searchDepartment')}
            emptyText={t('common.noDepartment')}
            disabled={isFetchingDepartments}
            triggerClassName="bg-secondary/50 text-xs"
            testId="product-form-owner-department"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('equipment.model.supportsOnlineStatus')}</Label>
          <div className="flex h-9 items-center">
            <Switch
              checked={form.supportsOnlineStatus}
              onCheckedChange={checked => setForm({ ...form, supportsOnlineStatus: checked })}
            />
          </div>
        </div>
        <div className="flex flex-col col-span-1 sm:col-span-2 gap-1.5">
          <Label className="text-xs">{t('equipment.model.detail')}</Label>
          <Textarea
            data-testid="product-form-description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="bg-secondary/50 text-xs"
            rows={2}
            placeholder=""
          />
        </div>
      </div>
      {/* <div className="flex flex-col gap-1.5">
            <Label className="text-xs">ประเภท</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-9 bg-secondary/50 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card text-foreground">
                <SelectItem value="durable">ครุภัณฑ์ถาวร (Durable)</SelectItem>
                <SelectItem value="consumable">วัสดุสิ้นเปลือง (Consumable)</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
    </FormDialog>
  )
}
